import classNames from 'classnames';
import React, {KeyboardEvent, useCallback} from 'react';

import {PencilIcon} from '../../../../resources/Icons';
import URLS from '../../../../urls';
import Col from '../../../common/Col';
import Row from '../../../common/Row';
import {ExtendedSupplier} from '../../marketplace/types';
import Button from '../buttons/Button';
import SquareButton from '../buttons/SquareButton';
import GenericCard from '../GenericCard';
import StarRatings from '../StarRatings';
import Tags from '../Tags';

export type SupplierCardProps = {
  className?: string;
  selected: boolean;
  supplier: ExtendedSupplier;
  onSelected: () => void;
  onOpen: () => void;
  showEditButton?: boolean;
  onShowInMap?: () => void;
  disabled?: boolean;
};

const SupplierCard = (props: SupplierCardProps) => {
  const {className, selected, supplier, onSelected, onOpen, showEditButton, onShowInMap, disabled} = props;

  const handleEditClick = useCallback((e: React.MouseEvent<any, MouseEvent>) => {
    e.stopPropagation();
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const enterKey = 'Enter';
      const spaceKey = 'Space';
      if (event.code === enterKey || event.code === spaceKey) {
        event.preventDefault();
        onSelected();
      }
    },
    [onSelected]
  );

  return (
    <GenericCard
      border={selected ? 'green' : 'light'}
      className={classNames('focus-invisible-within:outline-focus-visible', className)}
    >
      <Col
        className={classNames(
          'grow p-sm gap-[1rem] outline-none',
          !disabled && !selected ? 'hover:border-primary-300 cursor-pointer' : 'cursor-auto'
        )}
        onClick={disabled ? undefined : onSelected}
        onKeyDown={disabled || selected ? undefined : handleKeyDown}
        tabIndex={0}
        role={'button'}
        aria-disabled={disabled || selected}
      >
        <Row className={'grow gap-[1rem]'}>
          <Col className={'grow'}>
            <div className={'text-lg font-semibold leading-tight'}>{supplier.name}</div>
            {supplier.rating && <StarRatings rating={supplier.rating.average} count={supplier.rating.count} />}
          </Col>
          {showEditButton && (
            <SquareButton
              variant={'primary'}
              onClick={handleEditClick}
              href={URLS.commonApp.supplier.about}
              className={'-mt-[1rem] -mr-[1rem]'}
            >
              <PencilIcon className={'w-[1.4rem]'} />
            </SquareButton>
          )}
        </Row>
        <Tags tags={supplier.tags} expanded={selected} />
        <p className="text-sm">
          {supplier.address} · {supplier.town}
        </p>
        {selected && (
          <Row className={'gap-x-xs flex-wrap whitespace-nowrap'}>
            <Button className="grow mt-xs" variant="primary" onClick={onOpen} aria-disabled={false}>
              View full profile
            </Button>
            {onShowInMap && (
              <Button className="mt-xs" variant="secondary" onClick={onShowInMap}>
                Show in map
              </Button>
            )}
          </Row>
        )}
      </Col>
    </GenericCard>
  );
};

export default SupplierCard;
